import { useState } from "react";
import { DocumentState } from "@/services/backend/documents/types/state";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Download } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import {
  useErrValidationFailedContext,
  useWriteValidationError,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Summary } from "@/services/backend/htz/summary/types/summary";
import {
  SummaryDocument,
  useHtzSummaryPdfDownloadMutation,
  useLazyHtzSummaryPdfPreviewQuery,
  useHtzSummaryUpdateStateMutation,
} from "@/services/backend/htz/summary/api/summary-api";

export function SummaryPDF({ summary }: { summary: Summary }) {
  const previewRequest = {
    id: summary.id,
    document: SummaryDocument.SUMMARY,
  };
  const [loadPreview, { data, isLoading, isFetching, error }] =
    useLazyHtzSummaryPdfPreviewQuery();

  const { write } = useErrValidationFailedContext();

  const { user } = useAuth();

  // The preview is rendered by the backend. It is loaded when the sheet
  // opens, not on every auto-save. Reopening shows the last one until the
  // fresh one arrives.
  const onOpenChange = (open: boolean) => {
    if (open) {
      loadPreview(previewRequest);
    }
  };

  return (
    <DocumentSheet onOpenChange={onOpenChange}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Inspektionsbericht")}
              documentNumber={
                <ReferenceNumber referenceNumber={summary.referenceNumber} />
              }
            />
            <DocumentSheetActionsContent>
              {!isImmutable(summary) && (
                <>
                  <DocumentNotReadyAlert document={t("Inspektionsbericht")} />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Um eine Berichtnummer zu generieren müssen Sie den Inspektionsbericht festschreiben. Sobald der Inspektionsbericht festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: summary.id,
                      state: DocumentState.Immutable,
                      stateSetBy: user?.id || "",
                    }}
                    mutation={useHtzSummaryUpdateStateMutation}
                    description={t(
                      "Der Inspektionsbericht wird festgeschrieben und es wird eine Berichtnummer generiert. Danach kann der Inspektionsbericht nicht mehr geändert werden. Alle zugehörigen Anlagenberichte müssen dafür festgeschrieben sein.",
                    )}
                    onSuccess={() => loadPreview(previewRequest)}
                    onError={(err) => {
                      write(err);
                    }}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadSummaryDocumentButton
                  summaryId={summary.id}
                  document={SummaryDocument.SUMMARY}
                  text={t("Inspektionsbericht")}
                  description={t(
                    "Möchten Sie den Inspektionsbericht als PDF herunterladen?",
                  )}
                />
                <DownloadSummaryDocumentButton
                  summaryId={summary.id}
                  document={SummaryDocument.ALL}
                  text={t("Inspektionsbericht mit Anlagenberichten")}
                  description={t(
                    "Möchten Sie den Inspektionsbericht mit allen Anlagenberichten als PDF herunterladen? Alle Anlagenberichte müssen dafür festgeschrieben sein. Bei vielen Anlagenberichten ist die Datei sehr groß und das Erstellen kann einige Minuten dauern.",
                  )}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadSummaryDocumentButton({
  summaryId,
  document: documentType,
  text,
  description,
}: {
  summaryId: UUID;
  document: SummaryDocument;
  text: string;
  description: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useHtzSummaryPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useWriteValidationError(error, {
    onWrite: () => {
      setOpen(false);
      reset();
    },
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: summaryId, document: documentType, download: true });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Inspektionsbericht herunterladen")}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
